import {Affix, Collapse, Row, Tag} from "antd";
import {capitalizeFirstLetter, groupBy,} from "../utilities/functions";
import {CourseTable} from "./CourseTable";
import React, {useEffect} from "react";
import {PersonSpeciality} from "../backend/entity/study/PersonSpeciality";
import {CaretRightOutlined} from '@ant-design/icons';
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {RootDispatch, RootState} from "../state/rootStore";
import {Choice} from "../backend/entity/elective/Choice";
import {CourseTableType} from "./CourseTableType";
import {addElectiveChoices, ElectiveChoice} from "../state/electiveCoursesSlice";

const {Panel} = Collapse;

interface ElectiveChoiceCollapseProps {
    elective_choice: Choice[]
    study_person_speciality: PersonSpeciality[]
    areChoicesDisabled: boolean
    yearSemesterId: number
    howManyFutureSemesters: number
}

export default function ElectiveChoiceCollapse({
                                                   study_person_speciality,
                                                   areChoicesDisabled,
                                                   elective_choice,
                                                   yearSemesterId,
                                                   howManyFutureSemesters
                                               }: ElectiveChoiceCollapseProps) {

    const {t} = useTranslation();
    const dispatch = useDispatch<RootDispatch>()
    const electiveChoices = useSelector<RootState, ElectiveChoice[]>(state => state.electiveCourseReducer.electiveChoices)
    const firstSpecialityId = study_person_speciality[0]?.speciality.id

    useEffect(() => {
        dispatch(addElectiveChoices(elective_choice.map(ec => {
                const coursePersonSpeciality = study_person_speciality
                    .find(ps => ps.speciality.id === ec.speciality_id)
                return (
                    {
                        specialityId: ec.speciality_id,
                        courseId: ec.course_id,
                        courseEcts: coursePersonSpeciality ?
                            ec.course.course_semesters
                                .filter(cs => (cs.number === coursePersonSpeciality.semester.id) || cs.number === coursePersonSpeciality.semester.id + howManyFutureSemesters)
                                .map(cs => cs.ects).reduce((sum, el) => sum + el, 0)
                            : ec.course.course_semesters.map(cs => cs.ects).reduce((sum, el) => sum + el, 0),
                        decision: ec.decision,
                        electiveGroup: ec.elective_group
                    }
                )
            })
        ))
    }, [dispatch, elective_choice, study_person_speciality, howManyFutureSemesters]);

    const genExtra = (specialityId: number) => {
        const ectsSum = electiveChoices
            .filter(ec => ec.specialityId === specialityId)
            .map(ec => ec.courseEcts).reduce((a, b) => a + b, 0)
        return (
            <Row>
                <Affix offsetTop={78}>
                    <Tag style={{float: 'right', marginLeft: 4, marginRight: 0, marginBottom: 4}}
                         color={"geekblue"}>
                        {t('course.ects_sum')}: {ectsSum}
                    </Tag>
                </Affix>
            </Row>
        )
    };

    return (
        <Collapse
            accordion
            defaultActiveKey={firstSpecialityId}
            expandIcon={({isActive}) => <CaretRightOutlined rotate={isActive ? 90 : 0}/>}
        >
            {study_person_speciality.map((ps, idx) => {
                    const courses = ps.speciality.course_specialities.map(cs => cs.course)
                    const coursesGroups = groupBy(courses, 'elective_group')
                    return (
                        <Panel
                            header={ps.speciality.study.name
                                + ' —' + ps.speciality.name
                                + ' (' + ps.speciality.study.level
                                    .toLowerCase()
                                    .replace("ii_", "II ")
                                    .replace("i_", "I ")
                                + ') '
                                + ' - '
                                + t('elective_choice.for_semesters')
                                + ': ' + ps.semester.id
                                + (howManyFutureSemesters > 0 ? ' ' + t('and') + ' ' + (ps.semester.id + 1) : '')}
                            key={idx}
                            extra={genExtra(ps.speciality.id)}
                        > {
                            Object.keys(coursesGroups).sort().map((key, idx2) => {
                                const coursesByGroup = coursesGroups[key]
                                return (
                                    <CourseTable
                                        key={idx2}
                                        tableType={CourseTableType.ANY}
                                        title={capitalizeFirstLetter(key)}
                                        specialityId={ps.speciality.id}
                                        electiveGroup={key}
                                        courses={coursesByGroup}
                                        currentStudentSemester={ps.semester.id}
                                        areChoicesDisabled={areChoicesDisabled}
                                        howManyFutureSemesters={howManyFutureSemesters}
                                        yearSemesterId={yearSemesterId}
                                    />
                                )
                            })
                        }
                        </Panel>
                    )
                }
            )}
        </Collapse>
    )
}